import { useState, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { ConfirmDialogProvider } from "@/components/ui/confirm-dialog";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import SplashScreen from "@/components/SplashScreen";
import Layout from "@/components/Layout";
// Critical-path: login/forgot/reset stay EAGER so unauth users see them instantly.
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
// Everything below is lazy-split into separate chunks. The Marketing Hub alone
// drops ~400 KB of recharts + react-simple-maps + world-atlas TopoJSON from
// the main bundle.
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const TelecallerDashboard = lazy(() => import("@/pages/TelecallerDashboard"));
const SalesDashboard = lazy(() => import("@/pages/SalesDashboard"));
const MarketingDashboard = lazy(() => import("@/pages/MarketingDashboard"));
const SalesPipeline = lazy(() => import("@/pages/SalesPipeline"));
const LeadsList = lazy(() => import("@/pages/LeadsList"));
const LeadDetail = lazy(() => import("@/pages/LeadDetail"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const ChatPage = lazy(() => import("@/pages/ChatPage"));
const UserManagement = lazy(() => import("@/pages/UserManagement"));
const IntegrationsPage = lazy(() => import("@/pages/IntegrationsPage"));
const ContactsPage = lazy(() => import("@/pages/ContactsPage"));
const FollowUpPage = lazy(() => import("@/pages/FollowUpPage"));
const ActivityFeedPage = lazy(() => import("@/pages/ActivityFeedPage"));
const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const CompaniesPage = lazy(() => import("@/pages/CompaniesPage"));
const QuickActionsPage = lazy(() => import("@/pages/QuickActionsPage"));
const TasksPage = lazy(() => import("@/pages/TasksPage"));
const WorkReportsPage = lazy(() => import("@/pages/WorkReportsPage"));
const EmailTemplatesPage = lazy(() => import("@/pages/EmailTemplatesPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const TeamsPage = lazy(() => import("@/pages/TeamsPage"));
const PerformancePage = lazy(() => import("@/pages/PerformancePage"));
const HREmployees = lazy(() => import("@/pages/HREmployees"));
const HRAttendance = lazy(() => import("@/pages/HRAttendance"));
const HRLeaves = lazy(() => import("@/pages/HRLeaves"));
const HRLeavesHub = lazy(() => import("@/pages/HRLeavesHub"));
const HRPayroll = lazy(() => import("@/pages/HRPayroll"));
const HRHiring = lazy(() => import("@/pages/HRHiring"));
const HRHiringHub = lazy(() => import("@/pages/HRHiringHub"));
const MyLeaves = lazy(() => import("@/pages/MyLeaves"));
const LeaveCalendar = lazy(() => import("@/pages/LeaveCalendar"));
const InfraDashboard = lazy(() => import("@/pages/InfraDashboard"));
const InfraClients = lazy(() => import("@/pages/InfraClients"));
const InfraClientProfile = lazy(() => import("@/pages/InfraClientProfile"));
const FactoryDashboard = lazy(() => import("@/pages/FactoryDashboard"));
const FactoryOrderDetail = lazy(() => import("@/pages/FactoryOrderDetail"));
const MyFactoryOrders = lazy(() => import("@/pages/MyFactoryOrders"));
const FactorySupervisorDashboard = lazy(() => import("@/pages/FactorySupervisorDashboard"));
const HRSettings = lazy(() => import("@/pages/HRSettings"));
const HROnboarding = lazy(() => import("@/pages/HROnboarding"));
const HROnboardingSettings = lazy(() => import("@/pages/HROnboardingSettings"));
const OnboardingPrintPage = lazy(() => import("@/pages/OnboardingPrintPage"));
const OnboardingPrintBatchPage = lazy(() => import("@/pages/OnboardingPrintBatchPage"));
const HRLiveDashboard = lazy(() => import("@/pages/HRLiveDashboard"));
const RedateMetaLeads = lazy(() => import("@/pages/RedateMetaLeads"));
const MetaLeadForms = lazy(() => import("@/pages/MetaLeadForms"));
const AttendanceCheckIn = lazy(() => import("@/pages/AttendanceCheckIn"));
const OnboardingCandidatePage = lazy(() => import("@/pages/OnboardingCandidatePage"));
const WalkinApplicationPage = lazy(() => import("@/pages/WalkinApplicationPage"));
const WalkinLanding = lazy(() => import("@/pages/WalkinApplicationPage").then(m => ({ default: m.WalkinLandingPage })));
const DeletedFactoryOrders = lazy(() => import("@/pages/DeletedFactoryOrders"));
const MarketingExportsPage = lazy(() => import("@/pages/MarketingExportsPage"));
const MarketingLayout = lazy(() => import("@/pages/marketing/MarketingLayout"));
const MarketingOverview = lazy(() => import("@/pages/marketing/MarketingOverview"));
const MetaAdsDashboard = lazy(() => import("@/pages/marketing/MetaAdsDashboard"));
const GoogleAdsDashboard = lazy(() => import("@/pages/marketing/GoogleAdsDashboard"));
const WebsiteAnalyticsDashboard = lazy(() => import("@/pages/marketing/WebsiteAnalyticsDashboard"));
const IndiaMartDashboard = lazy(() => import("@/pages/marketing/IndiaMartDashboard"));
const MomDashboard = lazy(() => import("@/pages/marketing/MomDashboard"));
const EmployeePortal = lazy(() => import("@/pages/EmployeePortal"));
const HRAnnouncements = lazy(() => import("@/pages/HRAnnouncements"));
const HRAssets = lazy(() => import("@/pages/HRAssets"));
const HRReimbursements = lazy(() => import("@/pages/HRReimbursements"));
const HRAnalytics = lazy(() => import("@/pages/HRAnalytics"));
const HRCompliance = lazy(() => import("@/pages/HRCompliance"));
const HROffboarding = lazy(() => import("@/pages/HROffboarding"));
const HRDocLibrary = lazy(() => import("@/pages/HRDocLibrary"));
const HRPayrollEngine = lazy(() => import("@/pages/HRPayrollEngine"));
const EmployeeUserLink = lazy(() => import("@/pages/EmployeeUserLink"));
const HRWages = lazy(() => import("@/pages/HRWages"));
import { roleTemplate, userHasRole } from "@/lib/roleTemplate";

// Lightweight inline fallback while a route chunk downloads. Intentionally
// minimal so the perceived latency is "shimmer → content" not "spinner → spinner".
function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="h-8 w-8 border-2 border-[#155155]/20 border-t-[#155155] rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="h-10 w-10 border-3 border-[#155155]/20 border-t-[#155155] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (roles && !userHasRole(user, roles)) return <Navigate to="/dashboard" replace />;
  return children;
}

function DashboardRouter() {
  const { user } = useAuth();
  const t = roleTemplate(user);
  // Marketing-dept users (including custom roles) get a lead-funnel dashboard
  // instead of the sales-performance one.
  const isMarketingDept = (user?.department || "").toLowerCase().includes("marketing");
  if (isMarketingDept) return <MarketingDashboard />;
  if (t === "infrastructure_dept") return <Navigate to="/infrastructure" replace />;
  if (t === "telecaller") return <TelecallerDashboard />;
  if (t === "sales" || t === "manager") return <SalesDashboard />;
  if (t === "hr" || t === "junior_hr") return <Navigate to="/hr/employees" replace />;
  if (t === "factory_supervisor") return <Navigate to="/factory/supervisor-dashboard" replace />;
  if (t === "factory_manager") return <Navigate to="/factory" replace />;
  return <AdminDashboard />;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="h-10 w-10 border-3 border-[#155155]/20 border-t-[#155155] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500 font-body">Loading Ameenji CRM...</p>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route
        path="/forgot-password"
        element={user ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />}
      />
      <Route
        path="/reset-password"
        element={user ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />}
      />
      <Route path="/onboard/:token" element={<OnboardingCandidatePage />} />
      <Route path="/apply" element={<WalkinLanding />} />
      <Route path="/apply/:token" element={<WalkinApplicationPage />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardRouter />} />
        <Route path="/leads" element={<LeadsList />} />
        <Route path="/leads/:id" element={<LeadDetail />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/follow-ups" element={<FollowUpPage />} />
        <Route path="/activities" element={<ActivityFeedPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/companies" element={<ProtectedRoute roles={["admin", "sales", "manager"]}><CompaniesPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route
          path="/sales-pipeline"
          element={
            <ProtectedRoute roles={["admin", "sales", "manager"]}>
              <SalesPipeline />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute roles={["admin"]}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/work-reports" element={<WorkReportsPage />} />
        <Route
          path="/teams"
          element={
            <ProtectedRoute roles={["admin", "manager"]}>
              <TeamsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance"
          element={
            <ProtectedRoute roles={["admin", "manager"]}>
              <PerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/email-templates"
          element={
            <ProtectedRoute roles={["admin", "sales", "telecaller", "manager"]}>
              <EmailTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quick-actions"
          element={
            <ProtectedRoute roles={["sales", "telecaller", "manager"]}>
              <QuickActionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute roles={["admin"]}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations"
          element={
            <ProtectedRoute roles={["admin"]}>
              <IntegrationsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/hr/employees" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HREmployees /></ProtectedRoute>} />
        <Route path="/hr/attendance" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HRAttendance /></ProtectedRoute>} />
        <Route path="/hr/leaves" element={<ProtectedRoute roles={["admin", "hr", "junior_hr", "manager"]}><HRLeavesHub /></ProtectedRoute>} />
        <Route path="/hr/payroll" element={<ProtectedRoute roles={["admin", "hr"]}><HRPayroll /></ProtectedRoute>} />
        <Route path="/hr/hiring" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HRHiringHub /></ProtectedRoute>} />
        <Route path="/hr/onboarding" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HRHiringHub /></ProtectedRoute>} />
        <Route path="/hr/onboarding/print/:subId" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><OnboardingPrintPage /></ProtectedRoute>} />
        <Route path="/hr/onboarding/print-batch" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><OnboardingPrintBatchPage /></ProtectedRoute>} />
        <Route path="/hr/dashboard" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HRLiveDashboard /></ProtectedRoute>} />
        <Route path="/hr/onboarding/settings" element={<ProtectedRoute roles={["admin", "hr"]}><HROnboardingSettings /></ProtectedRoute>} />
        <Route path="/admin/redate-meta" element={<ProtectedRoute roles={["admin"]}><RedateMetaLeads /></ProtectedRoute>} />
        <Route path="/admin/meta-lead-forms" element={<ProtectedRoute roles={["admin"]}><MetaLeadForms /></ProtectedRoute>} />
        <Route path="/attendance/checkin" element={<ProtectedRoute><AttendanceCheckIn /></ProtectedRoute>} />
        <Route path="/hr/leave-calendar" element={<ProtectedRoute roles={["admin", "hr", "junior_hr", "manager"]}><HRLeavesHub /></ProtectedRoute>} />
        <Route path="/hr/settings" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HRSettings /></ProtectedRoute>} />
        <Route path="/factory/deleted-orders" element={<ProtectedRoute roles={["admin", "factory_manager"]}><DeletedFactoryOrders /></ProtectedRoute>} />
        <Route path="/my-leaves" element={<MyLeaves />} />
        <Route path="/portal" element={<ProtectedRoute><EmployeePortal /></ProtectedRoute>} />
        <Route path="/hr/announcements" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HRAnnouncements /></ProtectedRoute>} />
        <Route path="/hr/assets" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HRAssets /></ProtectedRoute>} />
        <Route path="/hr/reimbursements" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HRReimbursements /></ProtectedRoute>} />
        <Route path="/hr/analytics" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HRAnalytics /></ProtectedRoute>} />
        <Route path="/hr/compliance" element={<ProtectedRoute roles={["admin", "hr"]}><HRCompliance /></ProtectedRoute>} />
        <Route path="/hr/offboarding" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HROffboarding /></ProtectedRoute>} />
        <Route path="/hr/doc-library" element={<ProtectedRoute><HRDocLibrary /></ProtectedRoute>} />
        <Route path="/hr/payroll-engine" element={<ProtectedRoute roles={["admin", "hr"]}><HRPayrollEngine /></ProtectedRoute>} />
        <Route path="/hr/employee-user-link" element={<ProtectedRoute roles={["admin", "hr"]}><EmployeeUserLink /></ProtectedRoute>} />
        <Route path="/hr/wages" element={<ProtectedRoute roles={["admin", "hr", "junior_hr"]}><HRWages /></ProtectedRoute>} />
        <Route path="/infrastructure" element={<InfraDashboard />} />
        <Route path="/infrastructure/clients" element={<InfraClients />} />
        <Route path="/infrastructure/clients/:id" element={<InfraClientProfile />} />
        <Route path="/factory" element={<ProtectedRoute roles={["admin", "factory_manager", "factory_supervisor"]}><FactoryDashboard /></ProtectedRoute>} />
        <Route path="/factory/orders/:id" element={<FactoryOrderDetail />} />
        <Route path="/factory/my-orders" element={<MyFactoryOrders />} />
        <Route
          path="/factory/supervisor-dashboard"
          element={
            <ProtectedRoute roles={["factory_supervisor", "factory_manager", "admin"]}>
              <FactorySupervisorDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketing"
          element={
            <ProtectedRoute roles={["admin", "manager", "marketing"]}>
              <MarketingLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MarketingOverview />} />
          <Route path="meta" element={<MetaAdsDashboard />} />
          <Route path="google-ads" element={<GoogleAdsDashboard />} />
          <Route path="website" element={<WebsiteAnalyticsDashboard />} />
          <Route path="indiamart" element={<IndiaMartDashboard />} />
          <Route path="mom" element={<MomDashboard />} />
          <Route path="exports" element={<MarketingExportsPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  const [splashDone, setSplashDone] = useState(
    () => sessionStorage.getItem("ameenji_splash_done") === "true"
  );

  const handleSplashFinish = useCallback(() => {
    sessionStorage.setItem("ameenji_splash_done", "true");
    setSplashDone(true);
  }, []);

  return (
    <ErrorBoundary>
      {!splashDone && <SplashScreen onFinish={handleSplashFinish} />}
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <ConfirmDialogProvider>
              <AppRoutes />
              <PWAInstallPrompt />
            </ConfirmDialogProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
